import { useState, useRef, useEffect } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import {
  Rocket,
  TrendingUp,
  Zap,
  Building2,
  Target,
  ShieldCheck,
  Users,
  Star,
} from "lucide-react";

const MILESTONES = [
  {
    year: "2023",
    title: "A Vision",
    icon: Rocket,
    body: "Neuravolt was founded with the vision of building intelligent software and AI powered digital products for startups and businesses.",
    above: true,
  },
  {
    year: "2024",
    title: "Growth",
    icon: TrendingUp,
    body: "Expanded into web development, mobile applications, AI, cloud infrastructure, and delivered solutions across multiple industries.",
    above: false,
  },
  {
    year: "2025",
    title: "Momentum",
    icon: Zap,
    body: "Scaled into a full-service software engineering company with ERP, HRMS, CRM, IoT, enterprise software and automation solutions.",
    above: true,
  },
  {
    year: "2026",
    title: "The Evolution",
    icon: Building2,
    body: "Officially incorporated as VOLTNOVATE SOLUTIONS LLP and recognized as a Startup India (DPIIT) startup, positioning Neuravolt as an AI first software engineering company serving clients globally.",
    above: false,
  },
];

const PILLARS = [
  {
    icon: Target,
    title: "Purpose Driven",
    desc: "Solving real problems",
  },
  {
    icon: ShieldCheck,
    title: "Innovation First",
    desc: "Tech that creates impact",
  },
  {
    icon: Users,
    title: "Client Focused",
    desc: "Partnerships that grow",
  },
  {
    icon: Star,
    title: "Future Ready",
    desc: "Always evolving",
  },
];

export const Timeline = () => {
  const scrollContainerRef = useRef(null);

  // Scroll tracking across the sticky pinned container
  const { scrollYProgress } = useScroll({
    target: scrollContainerRef,
    offset: ["start start", "end end"],
  });

  // Smooth continuous progress for the glowing laser progress beam
  const smoothProgress = useSpring(scrollYProgress, { stiffness: 280, damping: 28 });
  const lineWidth = useTransform(smoothProgress, [0.04, 0.82], ["0%", "100%"]);

  // Tracks the highest reached card milestone so cards turn orange one by one only when their time arrives, and after remain orange
  // Initialized to -1 so cards are white initially before their time arrives
  const [maxStep, setMaxStep] = useState(-1);
  const [hoveredIdx, setHoveredIdx] = useState(null);

  useEffect(() => {
    const current = scrollYProgress.get();
    if (current >= 0.78) setMaxStep(3);
    else if (current >= 0.52) setMaxStep(2);
    else if (current >= 0.26) setMaxStep(1);
    else if (current >= 0.05) setMaxStep(0);

    const unsubscribe = scrollYProgress.on("change", (latest) => {
      if (latest >= 0.78) {
        setMaxStep((prev) => Math.max(prev, 3));
      } else if (latest >= 0.52) {
        setMaxStep((prev) => Math.max(prev, 2));
      } else if (latest >= 0.26) {
        setMaxStep((prev) => Math.max(prev, 1));
      } else if (latest >= 0.05) {
        setMaxStep((prev) => Math.max(prev, 0));
      }
    });
    return () => unsubscribe();
  }, [scrollYProgress]);

  return (
    <div
      ref={scrollContainerRef}
      data-testid="timeline"
      className="relative bg-[#FAF9F6] text-[#0B0B0B] h-[250vh] sm:h-[270vh]"
    >
      {/* ───────────────────────────────────────────────────────────── */}
      {/* STICKY PINNED VIEWPORT: Outer stays still while cards pop up   */}
      {/* ───────────────────────────────────────────────────────────── */}
      <div className="sticky top-0 h-screen w-full flex flex-col justify-center px-4 sm:px-6 lg:px-8 overflow-hidden select-none z-10">
        {/* Subtle Ambient Background Lighting */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute -top-32 left-10 w-[500px] h-[500px] bg-orange-400/5 blur-[140px] rounded-full" />
          <div className="absolute -bottom-32 right-10 w-[550px] h-[550px] bg-[#FF5A1F]/5 blur-[140px] rounded-full" />
        </div>

        <div className="max-w-7xl mx-auto w-full relative z-10 space-y-4 lg:space-y-5">
          {/* Section Header: Centered on the middle */}
          <div className="text-center mx-auto flex flex-col items-center">
            <motion.h2
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#0B0B0B] leading-[1.1]"
            >
              The Neuravolt{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF5A1F] to-[#FF7700]">
                Evolution
              </span>
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-neutral-600 mt-3 sm:mt-4 text-base sm:text-lg max-w-xl mx-auto font-normal"
            >
              From an idea to impact, every milestone shapes our future.
            </motion.p>
          </div>

          {/* ───────────────────────────────────────────────────────────── */}
          {/* CARD 1: BLACK TIMELINE CARD WITH AUTOMOTION EFFECT             */}
          {/* ───────────────────────────────────────────────────────────── */}
          <div className="relative rounded-[2rem] border border-white/10 bg-[#090b10] text-white p-5 sm:p-7 lg:p-8 shadow-[0_30px_90px_rgba(0,0,0,0.35),0_0_40px_rgba(255,90,31,0.06)] overflow-hidden">
            {/* Watermark numbers in background of black card */}
            <div className="absolute inset-0 pointer-events-none select-none overflow-hidden">
              <span className="absolute -left-4 sm:left-6 top-1/2 -translate-y-1/2 font-display text-[8rem] sm:text-[11rem] lg:text-[14rem] font-black text-white/[0.025] italic tracking-tighter">
                2023
              </span>
              <span className="absolute -right-4 sm:right-6 top-1/2 -translate-y-1/2 font-display text-[8rem] sm:text-[11rem] lg:text-[14rem] font-black text-white/[0.025] italic tracking-tighter">
                2026
              </span>
            </div>

            {/* Desktop 3-Tier Layout: Top Cards, Track Axis, Bottom Cards */}
            <div className="hidden lg:flex flex-col relative z-10 w-full py-1">
              {/* TIER 1: TOP CARDS ROW (2023 & 2025) */}
              <div className="grid grid-cols-4 gap-6 xl:gap-8 w-full min-h-[175px]">
                {/* Col 0: 2023 Card (Above) */}
                <div className="flex flex-col justify-end items-center w-full">
                  {renderDesktopCard(0)}
                </div>

                {/* Col 1: Empty slot above 2024 */}
                <div className="w-full" />

                {/* Col 2: 2025 Card (Above) */}
                <div className="flex flex-col justify-end items-center w-full">
                  {renderDesktopCard(2)}
                </div>

                {/* Col 3: Empty slot above 2026 */}
                <div className="w-full" />
              </div>

              {/* TIER 2: CENTRAL HORIZONTAL TRACK AXIS & 4 NODES */}
              <div className="relative w-full h-14 flex items-center my-0.5">
                {/* Base Line Track (extends cleanly across without arrow icon) */}
                <div className="absolute left-4 right-4 top-1/2 -translate-y-1/2 h-[2px] bg-white/15 rounded-full" />

                {/* Animated Orange Laser Progress Beam (Advances with scroll automotion) */}
                <motion.div
                  className="absolute left-4 top-1/2 -translate-y-1/2 h-[2.5px] rounded-full origin-left bg-gradient-to-r from-[#FF5A1F] via-[#FF7700] to-orange-400 shadow-[0_0_14px_rgba(255,90,31,0.85)] z-10"
                  style={{ width: lineWidth }}
                />

                {/* Continuous Pulse Beam */}
                <motion.div
                  className="absolute top-1/2 -translate-y-1/2 w-24 h-[4px] rounded-full bg-gradient-to-r from-transparent via-white to-transparent blur-[1px] z-20 pointer-events-none"
                  animate={{
                    left: ["0%", "92%"],
                    opacity: [0, 1, 0],
                  }}
                  transition={{
                    duration: 3.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                    repeatDelay: 1.5,
                  }}
                />

                {/* Left Start Ring */}
                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-3.5 h-3.5 rounded-full border-2 border-white/40 bg-[#090b10] z-20" />

                {/* 4 Nodes & Connectors */}
                <div className="grid grid-cols-4 gap-6 xl:gap-8 w-full h-full relative z-20">
                  {MILESTONES.map((m, i) => {
                    const isPopped = maxStep >= i;
                    const isAbove = m.above;

                    return (
                      <div
                        key={m.year}
                        className="relative flex items-center justify-center h-full w-full"
                      >
                        {/* Vertical Connector Line linking directly to the Card */}
                        <div
                          className={`absolute w-[2px] transition-all duration-500 ${
                            isAbove ? "top-0 h-1/2" : "top-1/2 bottom-0"
                          } ${
                            isPopped
                              ? "bg-[#FF5A1F] shadow-[0_0_8px_rgba(255,90,31,0.7)]"
                              : "bg-white/20"
                          }`}
                        />

                        {/* Node */}
                        <div
                          className="relative z-30 flex items-center justify-center cursor-pointer"
                          onMouseEnter={() => setHoveredIdx(i)}
                          onMouseLeave={() => setHoveredIdx(null)}
                        >
                          {/* Radar Pulse when Node activates */}
                          {isPopped && (
                            <motion.div
                              animate={{ scale: [1, 2.3], opacity: [0.75, 0] }}
                              transition={{ duration: 1.8, repeat: Infinity, ease: "easeOut" }}
                              className="absolute w-6 h-6 rounded-full border border-[#FF5A1F] pointer-events-none"
                            />
                          )}

                          <div
                            className={`w-5 h-5 rounded-full flex items-center justify-center transition-all duration-500 ${
                              isPopped
                                ? "bg-[#FF5A1F] ring-4 ring-[#FF5A1F]/30 shadow-[0_0_18px_rgba(255,90,31,0.95)] scale-110"
                                : "bg-[#090b10] border-2 border-white/50"
                            }`}
                          >
                            <div
                              className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                                isPopped ? "bg-white" : "bg-white/40"
                              }`}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* TIER 3: BOTTOM CARDS ROW (2024 & 2026) */}
              <div className="grid grid-cols-4 gap-6 xl:gap-8 w-full min-h-[175px]">
                {/* Col 0: Empty slot below 2023 */}
                <div className="w-full" />

                {/* Col 1: 2024 Card (Below) */}
                <div className="flex flex-col justify-start items-center w-full">
                  {renderDesktopCard(1)}
                </div>

                {/* Col 2: Empty slot below 2025 */}
                <div className="w-full" />

                {/* Col 3: 2026 Card (Below) */}
                <div className="flex flex-col justify-start items-center w-full">
                  {renderDesktopCard(3)}
                </div>
              </div>
            </div>

            {/* Mobile / Tablet View: Sequential Vertical Pop-Up Flow */}
            <div className="lg:hidden relative space-y-4 z-10 pl-3 sm:pl-5">
              <div className="absolute left-[24px] sm:left-[32px] top-6 bottom-6 w-[2px] bg-white/15 rounded-full" />

              <motion.div
                className="absolute left-[24px] sm:left-[32px] top-6 w-[2.5px] rounded-full origin-top bg-gradient-to-b from-[#FF5A1F] via-[#FF7700] to-orange-400 shadow-[0_0_12px_rgba(255,90,31,0.8)]"
                style={{ height: lineWidth }}
              />

              <div className="space-y-4">
                {MILESTONES.map((m, i) => {
                  const Icon = m.icon;
                  const isPopped = maxStep >= i;

                  return (
                    <div key={m.year} className="relative flex items-start gap-3.5 sm:gap-5">
                      <div
                        className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-4 z-10 transition-all duration-500 ${
                          isPopped
                            ? "bg-[#FF5A1F] ring-4 ring-[#FF5A1F]/30 shadow-[0_0_14px_rgba(255,90,31,0.9)]"
                            : "bg-[#090b10] border-2 border-white/50"
                        }`}
                      >
                        <div
                          className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                            isPopped ? "bg-white" : "bg-white/40"
                          }`}
                        />
                      </div>

                      {/* Pop Up Motion Card */}
                      <motion.div
                        animate={{
                          opacity: isPopped ? 1 : 0.4,
                          scale: isPopped ? 1 : 0.94,
                          y: isPopped ? 0 : 10,
                        }}
                        transition={{ type: "spring", stiffness: 300, damping: 24 }}
                        className={`flex-1 rounded-2xl p-4 sm:p-5 transition-all duration-500 border ${
                          isPopped
                            ? "bg-[#0d0f14] border-[#FF5A1F]/65 shadow-[0_12px_28px_rgba(0,0,0,0.8),0_0_22px_rgba(255,90,31,0.22)]"
                            : "bg-[#0c0e12] border-white/10 shadow-md"
                        }`}
                      >
                        <div className="flex items-center gap-3 mb-2">
                          <div
                            className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border transition-all duration-500 ${
                              isPopped
                                ? "bg-[#1c1512] border-[#FF5A1F]/50 text-[#FF5A1F] shadow-[0_0_12px_rgba(255,90,31,0.25)]"
                                : "bg-white/5 border-white/15 text-white"
                            }`}
                          >
                            <Icon className="w-5 h-5" />
                          </div>
                          <div>
                            <div
                              className={`font-display text-xl font-bold leading-tight transition-colors duration-500 ${
                                isPopped ? "text-[#FF5A1F] drop-shadow-[0_0_12px_rgba(255,90,31,0.6)]" : "text-white"
                              }`}
                            >
                              {m.year}
                            </div>
                            <div className="font-heading text-sm font-bold text-white">
                              {m.title}
                            </div>
                          </div>
                        </div>

                        <p
                          className={`text-xs leading-relaxed transition-colors duration-500 ${
                            isPopped ? "text-neutral-300" : "text-neutral-400"
                          }`}
                        >
                          {m.body}
                        </p>
                      </motion.div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* ───────────────────────────────────────────────────────────── */}
          {/* CARD 2: STILL ON ITS PREVIOUS POSITION/PLACE (NO MOTION)       */}
          {/* ───────────────────────────────────────────────────────────── */}
          <div className="rounded-2xl border border-white/10 bg-[#090b10] text-white p-4 sm:p-5 shadow-[0_20px_50px_rgba(0,0,0,0.3)]">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-4 divide-y sm:divide-y-0 sm:divide-x-0 lg:divide-x divide-white/10">
              {PILLARS.map((p, idx) => {
                const Icon = p.icon;
                return (
                  <div
                    key={idx}
                    className={`flex items-center gap-3.5 group ${
                      idx !== 0 ? "pt-3 sm:pt-0 lg:pl-6" : ""
                    }`}
                  >
                    <div className="w-9 h-9 rounded-xl bg-[#1c1512] border border-[#FF5A1F]/30 flex items-center justify-center text-[#FF5A1F] shrink-0 group-hover:scale-110 group-hover:border-[#FF5A1F]/60 group-hover:shadow-[0_0_15px_rgba(255,90,31,0.4)] transition-all duration-300">
                      <Icon className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="font-heading text-sm font-bold text-white group-hover:text-[#FF5A1F] transition-colors">
                        {p.title}
                      </div>
                      <div className="text-neutral-400 text-xs font-normal mt-0.5">
                        {p.desc}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Helper function to render each black card popping up in automotion on scroll
  function renderDesktopCard(i) {
    const m = MILESTONES[i];
    const Icon = m.icon;
    const isPopped = maxStep >= i;

    return (
      <motion.div
        data-testid={`tl-card-${i}`}
        onMouseEnter={() => setHoveredIdx(i)}
        onMouseLeave={() => setHoveredIdx(null)}
        initial={{ opacity: 0.35, scale: 0.9, y: m.above ? -20 : 20 }}
        animate={{
          // Turns orange and pops up only when its time arrives on scroll:
          opacity: isPopped ? 1 : 0.45,
          scale: isPopped ? (hoveredIdx === i ? 1.03 : 1) : 0.92,
          y: isPopped
            ? (hoveredIdx === i ? (m.above ? -5 : 5) : (m.above ? [-2, 2, -2] : [2, -2, 2]))
            : (m.above ? -15 : 15),
        }}
        transition={{
          opacity: { duration: 0.4 },
          scale: { type: "spring", stiffness: 320, damping: 22 },
          y: isPopped
            ? { duration: 4.5 + i * 0.4, repeat: Infinity, ease: "easeInOut" }
            : { duration: 0.3 },
        }}
        className={`w-full max-w-[270px] xl:max-w-[285px] rounded-2xl p-4 xl:p-5 transition-all duration-500 border ${
          isPopped
            ? "bg-[#0d0f14] border-[#FF5A1F]/65 shadow-[0_16px_35px_rgba(0,0,0,0.8),0_0_28px_rgba(255,90,31,0.25)]"
            : "bg-[#0c0e12] border-white/10 shadow-lg shadow-black/50"
        }`}
      >
        {/* Top Row: Icon + Year + Title */}
        <div className="flex items-center gap-3.5 mb-2.5">
          {/* Rounded Square Icon Badge: White before its time, turns to Orange only when its time arrives */}
          <div
            className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 border transition-all duration-500 ${
              isPopped
                ? "bg-[#1c1512] border-[#FF5A1F]/50 text-[#FF5A1F] shadow-[0_0_14px_rgba(255,90,31,0.3)] scale-105"
                : "bg-white/5 border-white/15 text-white"
            }`}
          >
            <Icon className="w-5 h-5" />
          </div>

          {/* Year & Title: Year is White before its time, turns to glowing Orange only when its time arrives */}
          <div>
            <div
              className={`font-display text-2xl font-bold leading-tight transition-all duration-500 ${
                isPopped
                  ? "text-[#FF5A1F] drop-shadow-[0_0_14px_rgba(255,90,31,0.65)]"
                  : "text-white"
              }`}
            >
              {m.year}
            </div>
            <div className="font-heading text-sm xl:text-base font-bold text-white leading-snug">
              {m.title}
            </div>
          </div>
        </div>

        {/* Description Body */}
        <p
          className={`text-xs xl:text-[13px] leading-relaxed font-normal transition-colors duration-500 ${
            isPopped ? "text-neutral-300" : "text-neutral-400"
          }`}
        >
          {m.body}
        </p>
      </motion.div>
    );
  }
};

export default Timeline;
